import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useSubscriptionPlans } from "@/features/fee-engine/api/use-subscription-plans";
import type { SubscriptionPlanFilter } from "@/features/fee-engine/api/dto";
import {
  SubscriptionPlanFormDialog,
  subscriptionPlansColumns,
  type SubscriptionPlanRow,
} from "@/features/fee-engine/components";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";

const toSubscriptionPlanFilter = (
  filters: ColumnFiltersState
): SubscriptionPlanFilter => {
  const filter: SubscriptionPlanFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "code") filter.code = value;
    // Le filtre `employees` (nombre) reste piloté par une saisie dédiée
    // (le coordinateur pourra ajouter un input hors table si besoin).
  }
  return filter;
};

/**
 * Liste paginée des plans d'abonnement entreprise
 * (`POST /SubscriptionPlans/all`, guide §6.5). Catalogue : écriture
 * réservée aux rôles flashpay-superadmin / flashpay-admin.
 */
export function SubscriptionPlansPage() {
  const { t } = useFeeEngineTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toSubscriptionPlanFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useSubscriptionPlans(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("fees.plans.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("fees.plans.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter(
          (p): p is SubscriptionPlanRow => Boolean(p.id)
        )}
        columnsClar={subscriptionPlansColumns}
        title={t("fees.plans.title")}
        exportFileName="subscription-plans"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <SubscriptionPlanFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </section>
  );
}
